import { motion } from "framer-motion";
import { useReveal } from "./useReveal";

/* ─── Shared animation variants ──────────────────────── */
export const itemVariant = {
  hidden: { opacity: 0, y: 40 },
  show:   { opacity: 1, y: 0,  transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] } },
};

const fadeUp = itemVariant;

/* ─── SectionLabel ────────────────────────────────────── */
/**
 * Small gold eyebrow text with a leading line.
 * @prop {boolean} dark  Use dark-mode gold (for light section backgrounds)
 * @prop {string}  className  Extra Tailwind classes
 */
export function SectionLabel({ children, dark = false, className = "" }) {
  const color = dark ? "#8b6220" : "#c9973a";
  return (
    <div
      className={`flex items-center gap-4 mb-6 ${className}`}
      style={{ color }}
    >
      <span style={{ width: "2rem", height: 1, background: color, display: "block", flexShrink: 0 }} />
      <span
        style={{
          fontFamily: "'Jost', sans-serif",
          fontSize: "0.68rem",
          fontWeight: 500,
          letterSpacing: "0.5em",
          textTransform: "uppercase",
        }}
      >
        {children}
      </span>
    </div>
  );
}

/* ─── SectionTitle ───────────────────────────────────── */
/**
 * Large serif heading.  Wrap italic words in <em> for gold colour.
 * @prop {boolean} dark
 * @prop {string}  className
 * Accepts `children` as a React node OR `dangerouslySetInnerHTML`.
 */
export function SectionTitle({ children, dark = false, className = "", dangerouslySetInnerHTML }) {
  const style = {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "clamp(2.2rem, 5vw, 3.8rem)",
    fontWeight: 300,
    lineHeight: 1.1,
    marginBottom: "1.5rem",
    color: dark ? "#1a1814" : "#f9f5ef",
  };

  const emStyle = `
    .monaco-title em {
      font-style: italic;
      color: ${dark ? "#8b6220" : "#f0c96b"};
    }
  `;

  if (dangerouslySetInnerHTML) {
    return (
      <>
        <style>{emStyle}</style>
        <h2
          className={`monaco-title ${className}`}
          style={style}
          dangerouslySetInnerHTML={dangerouslySetInnerHTML}
        />
      </>
    );
  }

  return (
    <>
      <style>{emStyle}</style>
      <h2 className={`monaco-title ${className}`} style={style}>
        {children}
      </h2>
    </>
  );
}

/* ─── Divider ────────────────────────────────────────── */
/**
 * Gold gradient line separator.
 */
export function Divider({ dark = false }) {
  return (
    <div
      style={{
        width: "4rem",
        height: 1,
        margin: "2rem 0",
        background: dark
          ? "linear-gradient(to right, #8b6220, transparent)"
          : "linear-gradient(to right, #c9973a, transparent)",
      }}
    />
  );
}

/* ─── RevealBox ──────────────────────────────────────── */
/**
 * Scroll-triggered fade-up wrapper. Fires once.
 * @prop {number} delay  seconds
 */
export function RevealBox({ children, delay = 0, className = "" }) {
  const { ref, inView } = useReveal();

  return (
    <motion.div
      ref={ref}
      variants={fadeUp}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      transition={{ delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── StaggerGrid ────────────────────────────────────── */
/**
 * Reveals children in a staggered cascade on scroll.
 * Children should use `variants={itemVariant}` (exported above).
 * @prop {number} stagger  seconds between each child
 */
export function StaggerGrid({ children, className = "", stagger = 0.12 }) {
  const { ref, inView } = useReveal();

  const container = {
    hidden: {},
    show:   { transition: { staggerChildren: stagger, delayChildren: 0.05 } },
  };

  return (
    <motion.div
      ref={ref}
      variants={container}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      className={className}
    >
      {children}
    </motion.div>
  );
}